/**
 * Community Events directory — filter by category, country, city, admission,
 * family-friendly, and date range. Shows upcoming events sorted by start_date.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http } from "@/lib/api";
import { Search, MapPin, Calendar, Plus, Users, DollarSign } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

function EventCard({ ev }) {
  return (
    <Link
      to={`/events/${ev.slug || ev.id}`}
      data-testid={`event-card-${ev.slug || ev.id}`}
      className="wofy-card !p-0 overflow-hidden flex flex-col group hover:shadow-xl transition-shadow"
    >
      <div
        className="w-full h-48 bg-cover bg-center relative"
        style={{
          backgroundImage: `url(${ev.cover_image || (ev.images && ev.images[0]) || ""})`,
          backgroundColor: "var(--wofy-surface)",
        }}
      >
        <span
          className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full"
          style={{ background: "rgba(253,251,247,0.92)", color: "var(--wofy-primary)", backdropFilter: "blur(4px)" }}
        >
          {ev.category}
        </span>
        {ev.admission === "Free" && (
          <span className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: "#065F46", color: "#D1FAE5" }}>
            FREE
          </span>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="overline mb-1 flex items-center gap-1.5" style={{ color: "var(--wofy-primary)" }}>
          <Calendar className="w-3 h-3" />
          {formatDate(ev.start_date)}
        </div>
        <h3 className="font-display text-lg leading-tight line-clamp-2">{ev.title}</h3>
        {ev.short_description && (
          <p className="mt-2 text-sm line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>{ev.short_description}</p>
        )}
        <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3 h-3" />
            <span className="line-clamp-1">
              {[ev.venue, ev.city, ev.country].filter(Boolean).join(" · ")}
            </span>
          </div>
          {ev.interested_count > 0 && (
            <div className="mt-1 flex items-center gap-1.5">
              <Users className="w-3 h-3" /> {ev.interested_count} interested
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Events() {
  const [items, setItems] = useState([]);
  const [taxonomy, setTaxonomy] = useState({});
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [country, setCountry] = useState("");
  const [admission, setAdmission] = useState("");
  const [familyFriendly, setFamilyFriendly] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/events/config").then((r) => setTaxonomy(r.data.taxonomy || {})).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 200 };
    if (q) params.q = q;
    if (category) params.category = category;
    if (country) params.country = country;
    if (admission) params.admission = admission;
    if (familyFriendly) params.family_friendly = true;
    const t = setTimeout(() => {
      http.get("/events", { params })
        .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
        .finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, category, country, admission, familyFriendly]);

  const categories = useMemo(() => Object.keys(taxonomy), [taxonomy]);
  const countries = useMemo(() => {
    const s = new Set(items.map((i) => i.country).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  return (
    <div data-testid="events-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <Helmet>
        <title>Community Events — Social Pulse</title>
        <meta name="description" content="Discover farmers markets, community cleanups, volunteer events, workshops, memorial walks and more, worldwide." />
        <link rel="canonical" href="https://socialpulse.com/events" />
        <meta property="og:title" content="Community Events — Social Pulse" />
        <meta property="og:description" content="Discover healthy, sustainable, educational, volunteer, and neighborhood events near you." />
      </Helmet>

      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="overline mb-3">COMMUNITY EVENTS</div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
            Every act of kindness has a calendar.
          </h1>
          <p className="mt-4 max-w-2xl text-base" style={{ color: "var(--wofy-text-soft)" }}>
            Farmers markets, community cleanups, walking groups, workshops, memorial walks and more —
            gathered from the world's neighborhoods. Submit your own event to reach volunteers globally.
          </p>
        </div>
        <Link to="/events/submit" data-testid="events-submit-cta" className="wofy-btn-primary shrink-0">
          <Plus className="w-4 h-4" /> Submit an event
        </Link>
      </div>

      {/* Filter bar */}
      <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
          <input
            data-testid="events-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, city, venue, or tag…"
            className="w-full pl-10 pr-3 py-2.5 rounded-full border bg-transparent text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
          />
        </div>
        <select
          data-testid="events-country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="rounded-full px-4 py-2.5 border text-sm bg-transparent"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="">All countries</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          data-testid="events-admission"
          value={admission}
          onChange={(e) => setAdmission(e.target.value)}
          className="rounded-full px-4 py-2.5 border text-sm bg-transparent"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="">Any admission</option>
          <option value="Free">Free only</option>
          <option value="Paid">Paid</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border" style={{ borderColor: "var(--wofy-border)" }}>
          <input
            type="checkbox"
            data-testid="events-family-friendly"
            checked={familyFriendly}
            onChange={(e) => setFamilyFriendly(e.target.checked)}
          />
          Family-friendly
        </label>
      </div>

      {/* Category chips */}
      <div className="mt-5 flex gap-2 flex-wrap">
        <button
          data-testid="events-cat-all"
          onClick={() => setCategory("")}
          className={`rounded-full px-3.5 py-1.5 text-xs border transition-colors ${!category ? "bg-[color:var(--wofy-text)] text-[color:var(--wofy-bg)] border-transparent" : "hover:bg-[color:var(--wofy-surface)]"}`}
          style={{ borderColor: "var(--wofy-border)" }}
        >
          All categories
        </button>
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`events-cat-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            onClick={() => setCategory(c)}
            className={`rounded-full px-3.5 py-1.5 text-xs border transition-colors ${category === c ? "bg-[color:var(--wofy-text)] text-[color:var(--wofy-bg)] border-transparent" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={{ borderColor: "var(--wofy-border)" }}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="mt-6 text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
        {loading ? "Loading…" : `Showing ${items.length} upcoming event${items.length === 1 ? "" : "s"}`}
      </div>

      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((ev) => <EventCard key={ev.id} ev={ev} />)}
      </div>

      {!loading && items.length === 0 && (
        <div className="mt-10 wofy-card text-center py-12" data-testid="events-empty">
          <div className="font-display text-xl">No events match your filters.</div>
          <button onClick={() => { setQ(""); setCategory(""); setCountry(""); setAdmission(""); setFamilyFriendly(false); }} className="wofy-btn-ghost mt-4">Reset filters</button>
        </div>
      )}
    </div>
  );
}
